import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBriefcase, FiCreditCard, FiDollarSign } from "react-icons/fi";

import "./ZYDoesTheRest.css";

const NOTIFICATION_CONFIG = [
  {
    id: "internship",
    icon: FiBriefcase,
    className: "is-internship",
  },
  {
    id: "loan",
    icon: FiDollarSign,
    className: "is-loan",
  },
  {
    id: "card",
    icon: FiCreditCard,
    className: "is-card",
  },
];

function ZYNotification({ icon: Icon, message, time, className, index }) {
  return (
    <article
      className={["zy-rest-notification", className].join(" ")}
      style={{
        "--notification-delay": `${index * 180}ms`,
      }}
    >
      <div className="zy-rest-notification__icon">
        <Icon />
      </div>

      <div className="zy-rest-notification__body">
        <div className="zy-rest-notification__header">
          <strong>ZY</strong>
          <span>{time}</span>
        </div>

        <p>{message}</p>
      </div>
    </article>
  );
}

export default function ZYDoesTheRest() {
  const { t } = useTranslation("home");

  const sectionRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);

  const notifications = useMemo(() => {
    const translatedNotifications = t("zyDoesTheRest.notifications", {
      returnObjects: true,
    });

    if (!Array.isArray(translatedNotifications)) {
      return [];
    }

    return NOTIFICATION_CONFIG.map((configItem) => {
      const translatedItem = translatedNotifications.find(
        (item) => item.id === configItem.id,
      );

      return {
        ...configItem,
        message: translatedItem?.message ?? "",
        time: translatedItem?.time ?? "",
      };
    });
  }, [t]);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setIsVisible(true);
        observer.disconnect();
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={["zy-rest", isVisible ? "is-visible" : ""]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby="zy-rest-title"
      aria-label={t("zyDoesTheRest.accessibility.sectionLabel")}
    >
      <div className="zy-rest__background-glow" aria-hidden="true" />

      <div className="zy-rest__inner">
        <div className="zy-rest__content">
          <span className="zy-rest__eyebrow">{t("zyDoesTheRest.eyebrow")}</span>

          <h2 id="zy-rest-title">
            {t("zyDoesTheRest.title.line1")}
            <br />
            {t("zyDoesTheRest.title.line2")}
          </h2>
        </div>

        <div className="zy-rest__notifications">
          {notifications.map((notification, index) => (
            <ZYNotification
              key={notification.id}
              {...notification}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
